import type { FeatureAccess } from './access';

export interface ShareOptions {
  allowScene: boolean;
  allowDownload: boolean;
  allowPointCloudNotes: boolean;
  allowGaussianNotes: boolean;
  allowOrthoNotes: boolean;
  allowPointCloudMeasurements: boolean;
  allowOrthoMeasurements: boolean;
}

export type ShareOptionKey = keyof ShareOptions;

// The Scene starts on: every link created before this option could open it.
export const DEFAULT_SHARE_OPTIONS: ShareOptions = {
  allowScene: true,
  allowDownload: false,
  allowPointCloudNotes: false,
  allowGaussianNotes: false,
  allowOrthoNotes: false,
  allowPointCloudMeasurements: false,
  allowOrthoMeasurements: false,
};

export interface ShareOptionDef { key: ShareOptionKey; title: string; hint: string }

export const SHARE_OPTION_GROUPS: { title: string | null; note?: string; options: ShareOptionDef[] }[] = [
  {
    title: null,
    options: [
      { key: 'allowScene', title: 'Allow opening the Scene', hint: 'Recipients get an Open Scene button and can explore the files in 3D' },
      { key: 'allowDownload', title: 'Allow file download', hint: 'Recipients can download files from this link' },
    ],
  },
  {
    title: 'Notes',
    options: [
      { key: 'allowPointCloudNotes', title: 'Allow adding notes in Point Clouds', hint: 'Recipients can add notes and replies in the Scene' },
      { key: 'allowGaussianNotes', title: 'Allow adding notes in Gaussian Splats', hint: 'Recipients can add notes and replies in the photoreal viewer' },
      { key: 'allowOrthoNotes', title: 'Allow adding notes in Orthomosaics', hint: 'Recipients can add notes and replies in the orthomosaic viewer' },
    ],
  },
  {
    title: 'Measurements',
    note: 'Saved measurements are always visible on the link',
    options: [
      { key: 'allowPointCloudMeasurements', title: 'Allow adding measurements in Point Clouds', hint: 'Recipients can add their own distance and area measurements in the Scene' },
      { key: 'allowOrthoMeasurements', title: 'Allow adding measurements in Orthomosaics', hint: 'Recipients can add their own measurements in the orthomosaic viewer and the Scene' },
    ],
  },
];

// Point clouds are shown only in the Scene.
const NEEDS_SCENE: ShareOptionKey[] = ['allowPointCloudNotes', 'allowPointCloudMeasurements'];

export const shareOptionNeedsScene = (key: ShareOptionKey) => NEEDS_SCENE.includes(key);

export const shareOptionsInput = (opts: ShareOptions): ShareOptions => ({
  ...opts,
  allowPointCloudNotes: opts.allowScene && opts.allowPointCloudNotes,
  allowPointCloudMeasurements: opts.allowScene && opts.allowPointCloudMeasurements,
});

export type SharedSceneState = 'open' | 'linkOff' | 'ownerOff' | 'empty';

interface SharedSceneFile { type: string; status?: string | null; extension?: string | null }

interface SharedSceneLink {
  allowScene?: boolean | null;
  ownerAccess?: FeatureAccess | null;
  files?: SharedSceneFile[] | null;
}

// What a share link's Scene can draw: share links load no BIM models, only
// DWG/DXF drawings are layers, and splats / orthomosaics need the project
// owner's access to them.
const sharedSceneContent = (f: SharedSceneFile, access: FeatureAccess) =>
  f.status === 'ready' && (
    f.type === 'point_cloud'
    || (f.type === 'two_d' && ['dwg', 'dxf'].includes((f.extension ?? '').toLowerCase()))
    || (f.type === 'orthomosaic' && access.ortho)
    || (f.type === 'gaussian' && access.splat)
  );

// One rule for the share page's Open Scene button and the shared Scene route.
export const sharedSceneState = (link: SharedSceneLink): SharedSceneState => {
  if (link.allowScene === false) return 'linkOff';
  const access = link.ownerAccess;
  if (!access?.scene) return 'ownerOff';
  return (link.files ?? []).some((f) => sharedSceneContent(f, access)) ? 'open' : 'empty';
};
